import { NearbyRadius, type RestaurantDetails } from "@/API";
import type { State } from "@/types/common";
import { create } from "zustand";
import { generateClient, type GraphQLResult } from "aws-amplify/api";
import { getRestaurantDetails as getRestaurantDetailsQuery } from "@/graphql/queries";
import { setActionTriggerNeedCore } from "./restrictedFeatureState";

const client = generateClient();

export const restaurantDetailsState = create<State<RestaurantDetails>>(() => ({
  data: null,
  isLoading: false,
  error: null,
  isFetching: false,
}));

export const getrestaurantDetailsIsloading = () =>
  restaurantDetailsState.setState(() => ({
    isFetching: false,
    isLoading: true,
    error: null,
    data: null,
  }));
export const getrestaurantDetailsIsFetching = () =>
  restaurantDetailsState.setState(() => ({
    isFetching: true,
    error: null,
    isLoading: false,
  }));
export const getRestaurantDetailsIsError = (error: Error) =>
  restaurantDetailsState.setState(() => ({
    isFetching: false,
    error: error,
    data: null,
    isLoading: false,
  }));
export const getRestaurantDetailsIsSuccess = (
  restaurantDetails: RestaurantDetails,
) =>
  restaurantDetailsState.setState(() => ({
    isFetching: false,
    data: restaurantDetails,
    isLoading: false,
    error: null,
  }));

export const resetRestaurantDetailsState = () =>
  restaurantDetailsState.setState(() =>
    restaurantDetailsState.getInitialState(),
  );

export type getRestaurantDetailsParams = {
  id: string;
  nearbyRadius?: NearbyRadius;
  nearbyTagsInCommon?: number;
  tagsExactMatch?: string[];
};

export const getRestaurantDetails = async ({
  id,
  nearbyRadius,
  nearbyTagsInCommon = 1,
  tagsExactMatch,
}: getRestaurantDetailsParams) => {
  const fetchSameRestaurant =
    restaurantDetailsState.getState()?.data?.id === id;

  if (fetchSameRestaurant) getrestaurantDetailsIsFetching();

  if (!fetchSameRestaurant) getrestaurantDetailsIsloading();

  try {
    const { data } = await client.graphql({
      query: getRestaurantDetailsQuery,
      variables: {
        id,
        nearbyTagsInCommon,
        nearbyRadius: nearbyRadius
          ? nearbyRadius
          : NearbyRadius.TWO_HUNDRED_METERS,
        tagsExactMatch,
      },
    });

    getRestaurantDetailsIsSuccess(
      data.getRestaurantDetails as RestaurantDetails,
    );
  } catch (e) {
    const errorMessage =
      e instanceof Error ? e.message : "failed to query get restaurant details";
    getRestaurantDetailsIsError(Error(errorMessage));

    const graphQl = e as GraphQLResult<RestaurantDetails>;

    const restaurantsError = graphQl.errors;
    if (
      restaurantsError &&
      !(restaurantsError instanceof Error) &&
      restaurantsError[0] &&
      restaurantsError[0].message ===
        "Free view restaurant details quota exceeded"
    ) {
      setActionTriggerNeedCore(true);
    }
  }
};
